import { useState, useEffect, useCallback } from 'react';
import useUser from '@/hooks/useUser';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { SavingsGameState, SavingsGoal, SavingsStrategy, INITIAL_STATE } from './types';
import { supabase } from '@/lib/supabase';
import { calculateSavingsPoints, GAME_POINTS } from '@/lib/cerebrix-config';

export const useSavingsGame = () => {
  const [state, setState] = useState<SavingsGameState>(INITIAL_STATE);
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Obtener el ID del juego
  const { data: gameId } = useQuery({
    queryKey: ['game-id', 'savings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('games')
        .select('id')
        .eq('title', 'Ahorro Inteligente')
        .single();
      
      if (error) {
        console.error('Error fetching game ID:', error);
        return null;
      }
      
      return data?.id;
    },
    enabled: !!user?.id,
    staleTime: Infinity
  });

  const startGame = useCallback(() => {
    setState({
      ...INITIAL_STATE,
      gameStarted: true
    });
  }, []);

  const selectGoal = useCallback((goal: SavingsGoal) => {
    setState(prev => ({
      ...prev,
      selectedGoal: goal,
      stats: {
        ...prev.stats,
        hasSmartGoal: true // La meta es SMART por defecto ya que están predefinidas
      }
    }));
  }, []);

  const selectStrategy = useCallback((strategy: SavingsStrategy) => {
    setState(prev => {
      if (!prev.selectedGoal) return prev;

      const monthlyIncome = 500; // Mesada base
      const monthlyAmount = strategy.type === 'fixed' 
        ? strategy.value 
        : (monthlyIncome * strategy.value) / 100;

      // Siempre crear exactamente 6 períodos, independientemente del timeFrame de la meta
      const periods = Array.from({ length: 6 }, (_, i) => ({
        month: i + 1,
        plannedAmount: monthlyAmount,
        actualAmount: 0,
        completed: false
      }));

      return {
        ...prev,
        selectedStrategy: {
          ...strategy,
          monthlyAmount
        },
        periods,
        stats: {
          ...prev.stats,
          hasStrategy: true
        }
      };
    });
  }, []);

  const addSaving = useCallback((amount: number) => {
    setState(prev => {
      if (!prev.selectedGoal || !prev.selectedStrategy) return prev;

      // Limitar la cantidad que se puede ahorrar en una sola acción
      // basado en el monto total de la meta y el tiempo restante
      const maxSingleSaving = prev.selectedGoal.amount * 0.25; // Máximo 25% de la meta en un solo aporte
      const adjustedAmount = Math.min(amount, maxSingleSaving);
      
      // Si el usuario intenta ahorrar más del límite, mostrar un mensaje
      if (amount > maxSingleSaving) {
        toast({
          title: "¡Ahorro limitado!",
          description: `Solo puedes ahorrar hasta ${maxSingleSaving.toFixed(0)} en una sola acción. Intenta ahorros más pequeños y constantes.`,
          variant: "warning",
          duration: 4000
        });
      }

      const newSavedAmount = prev.savedAmount + adjustedAmount;
      const goalCompleted = newSavedAmount >= prev.selectedGoal.amount;
      
      // Calcular puntos basados en la acción
      const basePoints = 10; // Puntos base por cada acción de ahorro
      const newScore = prev.score + basePoints;
      
      // Actualizar los períodos para marcar la consistencia
      const newPeriods = [...prev.periods];
      
      // Encontrar el período actual basado en el tiempo transcurrido
      const totalPeriods = 6; // Siempre 6 períodos
      const timeElapsed = 300 - prev.timeLeft; // Tiempo transcurrido en segundos
      const totalTime = 300; // Tiempo total del juego en segundos
      
      // Calcular el período actual basado en el porcentaje de tiempo transcurrido
      const currentPeriodIndex = Math.min(
        Math.floor((timeElapsed / totalTime) * totalPeriods),
        totalPeriods - 1
      );
      
      // Actualizar el período actual
      if (currentPeriodIndex >= 0 && currentPeriodIndex < newPeriods.length) {
        // Calcular el nuevo monto acumulado para este período
        const newAmount = newPeriods[currentPeriodIndex].actualAmount + adjustedAmount;
        
        // Acumular el ahorro en el período actual
        newPeriods[currentPeriodIndex] = {
          ...newPeriods[currentPeriodIndex],
          actualAmount: newAmount,
          // Marcar como completado si se ha realizado al menos un ahorro en este período
          completed: true
        };
        
        // Mostrar mensaje de confirmación solo si el período no estaba completado antes
        if (!prev.periods[currentPeriodIndex].completed) {
          toast({
            title: "¡Período completado!",
            description: `Has completado el período ${currentPeriodIndex + 1} de ${totalPeriods}. ¡Sigue así para obtener puntos por consistencia!`,
            variant: "success",
            duration: 3000
          });
        }
      }
      
      // Verificar si los ahorros son consistentes (al menos 80% de los períodos completados)
      const completedPeriods = newPeriods.filter(p => p.completed).length;
      const consistentSavings = completedPeriods >= Math.ceil(totalPeriods * 0.8);
      
      return {
        ...prev,
        savedAmount: newSavedAmount,
        totalSaved: newSavedAmount,
        score: newScore,
        periods: newPeriods,
        stats: {
          ...prev.stats,
          goalCompleted,
          consistentSavings
        }
      };
    });
  }, [toast]);

  const updateProgress = useMutation({
    mutationFn: async () => {
      if (!user?.id || !gameId) {
        throw new Error('No session or game ID found');
      }

      // Calcular puntuación final basada en las estadísticas
      let finalScore = 0;
      
      if (state.stats.goalCompleted) {
        finalScore += GAME_POINTS.SAVINGS.COMPLETION;
      }
      
      if (state.stats.hasSmartGoal) {
        finalScore += GAME_POINTS.SAVINGS.SMART_GOAL;
      }
      
      if (state.stats.hasStrategy) {
        finalScore += GAME_POINTS.SAVINGS.STRATEGY;
      }
      
      if (state.stats.consistentSavings) {
        finalScore += GAME_POINTS.SAVINGS.CONSISTENCY;
      }

      // Calcular Cerebrix ganados (ahora los puntos base son los mismos que se muestran)
      const basePoints = calculateSavingsPoints({
        goalCompleted: state.stats.goalCompleted,
        hasSmartGoal: state.stats.hasSmartGoal,
        hasStrategy: state.stats.hasStrategy,
        consistentSavings: state.stats.consistentSavings,
        timeUsed: 300 - state.timeLeft,
        maxTime: 300
      });

      console.log('Registrando sesión con:', {
        userId: user.id,
        gameId,
        finalScore,
        basePoints,
        stats: state.stats
      });

      // Intentar registrar la sesión varias veces si falla
      let sessionResult = null;
      let error = null;
      
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          console.log('Intentando registrar sesión, intento:', attempt + 1);
          
          // Registrar la sesión del juego
          const response = await supabase.rpc('register_game_session', {
            p_user_id: user.id,
            p_game_id: gameId,
            p_points_earned: finalScore,
            p_session_duration: 300 - state.timeLeft,
            p_difficulty_level: 'beginner',
            p_bonus_multiplier: 1.0
          });

          console.log('Respuesta de registro:', response);

          if (response.error) {
            console.error('Error en la respuesta:', response.error);
            throw response.error;
          }
          
          sessionResult = response.data;
          console.log('Resultado de la sesión:', sessionResult);
          break;
        } catch (e) {
          console.error('Error en intento', attempt + 1, ':', e);
          error = e;
          if (attempt < 2) await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      if (error && !sessionResult) {
        throw error;
      }

      // Actualizar progreso del juego
      const { error: progressError } = await supabase
        .from('game_progress')
        .upsert({
          user_id: user.id,
          game_id: gameId,
          high_score: state.savedAmount,
          total_plays: 1,
          last_played_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,game_id'
        });

      if (progressError) throw progressError;

      // Obtener los Cerebrix convertidos del resultado
      const cerebrixEarned = sessionResult && typeof sessionResult.cerebrix_converted === 'number' 
        ? sessionResult.cerebrix_converted 
        : 0;

      return {
        cerebrixEarned,
        stats: state.stats,
        goalName: state.selectedGoal?.name,
        savedAmount: state.savedAmount,
        finalScore
      };
    },
    onSuccess: (data) => {
      // Actualizar las cachés de React Query
      queryClient.invalidateQueries({ queryKey: ['user-points'] });
      queryClient.invalidateQueries({ queryKey: ['user-cerebrix'] });
      queryClient.invalidateQueries({ queryKey: ['cerebrixStats'] });
      
      // Actualizar el estado con los Cerebrix ganados y marcar el juego como terminado
      setState(prev => ({
        ...prev,
        cerebrixEarned: data.cerebrixEarned,
        isGameOver: true,
        score: data.finalScore,
        stats: data.stats
      }));
      
      // Ya no mostramos el toast porque ahora tenemos un componente visual para mostrar los resultados
    },
    onError: (error) => {
      console.error('Error updating progress:', error);
      toast({
        title: "Error al guardar progreso",
        description: "No se pudo guardar tu progreso. Por favor intenta de nuevo.",
        variant: "destructive",
        duration: 4000
      });
    }
  });

  // Timer del juego
  useEffect(() => {
    if (!state.gameStarted || state.isGameOver) return;

    const timer = setInterval(() => {
      setState(prev => {
        const newTimeLeft = Math.max(0, prev.timeLeft - 1);
        
        // Verificar si ha cambiado el período
        const totalPeriods = 6; // Siempre 6 períodos
        const periodDuration = 300 / totalPeriods; // Duración de cada período en segundos
        
        const oldPeriodIndex = Math.min(
          Math.floor((300 - prev.timeLeft) / periodDuration),
          totalPeriods - 1
        );
        
        const newPeriodIndex = Math.min(
          Math.floor((300 - newTimeLeft) / periodDuration),
          totalPeriods - 1
        );
        
        // Si cambiamos de período, mostrar una notificación
        if (newPeriodIndex > oldPeriodIndex && newTimeLeft > 0) {
          toast({
            title: `¡Nuevo período de ahorro!`,
            description: `Ahora estás en el período ${newPeriodIndex + 1} de ${totalPeriods}. ¡Haz un ahorro para completarlo!`,
            variant: "info",
            duration: 4000
          });
        }
        
        // Si el tiempo se acabó, finalizar el juego
        if (newTimeLeft === 0) {
          // Llamar a updateProgress para registrar los resultados
          setTimeout(() => {
            updateProgress.mutate();
          }, 500);
          
          return {
            ...prev,
            timeLeft: 0,
            isGameOver: true
          };
        }
        
        return {
          ...prev,
          timeLeft: newTimeLeft
        };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [state.gameStarted, state.isGameOver, toast, updateProgress]);

  return {
    state,
    startGame,
    selectGoal,
    selectStrategy,
    addSaving,
    updateProgress
  };
};
